import { Injectable } from '@angular/core';
import { ApiService, Pagination, PER_PAGE } from './api.service';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, shareReplay, Subject, switchMap, tap } from 'rxjs';
import { SalesOrder } from '@shared/entities/salesOrder';
import { Paginate } from '@shared/entities/paginate';
import { SalesOrderFiltersService, SalesOrderFilterURL } from './sales-order-filters.service';

const LOG_PREFIX = '[Api][SalesOrder] ';

@Injectable({
  providedIn: 'root'
})
export class SalesOrderService extends ApiService<SalesOrder> {
  private _refresh$: Subject<Pagination>;
  all$: Observable<Paginate<SalesOrder>>;
  filters$: Observable<any>;
  private _currentFilters: SalesOrderFilterURL = {};

  private _currentPaginator: Pagination;

  set filters(value: SalesOrderFilterURL) {
    this._currentFilters = value;
    this.refresh({ page: 1, perPage: PER_PAGE });
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private _salesOrderFiltersService: SalesOrderFiltersService
  ) {
    const masterUrl = '/sales-orders';
    const detailUrl = '/sales-orders';

    super(SalesOrder, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);

    this.filters$ = this._salesOrderFiltersService.all$;

    this.all$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this.activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }

        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.salesOrderNumber !== undefined) {
          this.setUrlParameter('salesOrderNumber', this._currentFilters.salesOrderNumber);
        }
        if (this._currentFilters.accountId !== undefined) {
          this.setUrlParameter('accountId', this._currentFilters.accountId);
        }
        if (this._currentFilters.accountName !== undefined) {
          this.setUrlParameter('accountName', this._currentFilters.accountName);
        }
        if (this._currentFilters.serialNumbers !== undefined) {
          this.setUrlParameter('serialNumbers', this._currentFilters.serialNumbers);
        }

        const orderBy = this._currentFilters.orderBy || 'createdDate';
        this.setUrlParameter('order_by', orderBy);

        if (orderBy === 'salesOrderNumber') {
          this.setUrlParameter('direction', 'ASC');
        } else if (orderBy === 'accountId') {
          this.setUrlParameter('direction', 'ASC');
        } else {
          this.setUrlParameter('direction', 'DESC');
        }

        return this.paginate(SalesOrder, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ sales order', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ sales order error', err)
      }),
      shareReplay(1)
    );
  }

  refresh(pagination?: Pagination): void {
    const newPagination = pagination || this._currentPaginator;
    this._currentPaginator = newPagination;
    this._refresh$.next(newPagination);
  }
}
